import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CircleNotchIcon } from '@phosphor-icons/react';
import { UsersTable } from '../components/admin/UsersTable';
import { UsersFilters } from '../components/admin/UsersFilters';
import { GroupDetailHeader } from '../components/admin/GroupDetailHeader';
import { Pagination } from '../components/ui/Pagination';
import { AddMemberToGroupModal } from '../components/admin/AddMemberToGroupModal';
import { useGroupDetailController } from '../hooks/useGroupDetailController';

export const GroupDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    selectedCompanyId,
    groupId,
    canManage,
    groupData,
    isGroupLoading,
    groupError,
    search,
    setSearch,
    roleFilter,
    handleRoleFilterChange,
    currentPage,
    setCurrentPage,
    paginatedMembers,
    totalPages,
    addMemberModalRef,
    handleUserAction,
    handleBulkResend,
    handleBulkRemove,
    handleRefresh,
    handleAddMember,
    handleMembersAdded,
  } = useGroupDetailController();

  if (!selectedCompanyId || !groupId) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <p className="text-gray-500">{t('common.error')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <GroupDetailHeader
          groupName={groupData?.name}
          memberCount={groupData?.memberCount}
          canManage={canManage}
          onBack={() => navigate('/admin/users?tab=groups')}
          onRefresh={handleRefresh}
          onAddMember={handleAddMember}
        />

        <UsersFilters
          search={search}
          onSearchChange={setSearch}
          roleFilter={roleFilter}
          onRoleFilterChange={handleRoleFilterChange}
        />

        {isGroupLoading && (
          <div className="flex justify-center items-center py-12">
            <CircleNotchIcon size={32} className="animate-spin text-primary" />
          </div>
        )}

        {groupError && <div className="p-6 text-center text-red-600">{t('common.error')}</div>}
      </div>

      {!isGroupLoading && !groupError && groupData && (
        <>
          <UsersTable
            users={paginatedMembers}
            onUserAction={handleUserAction}
            isCurrentUserAdmin={canManage}
            removeActionLabel={t('admin.group_detail.actions.remove_member')}
            onBulkResend={canManage ? handleBulkResend : undefined}
            onBulkRemove={canManage ? handleBulkRemove : undefined}
          />

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </>
      )}

      {selectedCompanyId && groupId && (
        <AddMemberToGroupModal
          ref={addMemberModalRef}
          companyId={selectedCompanyId}
          groupId={groupId}
          existingMemberIds={groupData?.members.map((m) => m.id) || []}
          onSuccess={handleMembersAdded}
        />
      )}
    </div>
  );
};
